// MUI-free copy of Ui-components/Banner.
import React, {memo} from 'react';
import Image from 'next/image';
import type {StaticImageData} from 'next/image';
import Head from 'next/head';
import {cdnImage} from '@/util/cdnImage';
import Container from '@/components/atoms/Container';
import ArrowButton from '@/components/atoms/ArrowButton';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import clutchBadge from 'public/images/review-badges/Clutch-Review-bagdes-logo.webp';
import goodfirmsBadge from 'public/images/review-badges/Goodfirms-Review-bagdes-logo.webp';
import topDevBadge from 'public/images/review-badges/Top-Developer-bagdes-logo.webp';
import styles from './Banner.module.scss';

const BANNER_IMAGE_WIDTH = 654;
// Lower-case DOM attribute: React 18 doesn't know `fetchPriority` (next/image writes it the same way).
const HIGH_FETCH_PRIORITY = {fetchpriority: 'high'} as Record<string, string>;
// On phones the hero image sits below the first screen (under the heading, points and buttons), while the heading
// text is the LCP element; preloading the image there only competes with it. So the high-priority preload applies
// from 600px up, where the image sits beside the heading.
const DESKTOP_BANNER_MEDIA = '(min-width: 600px)';

/**
 * The `srcSet` next/image (13.4, default loader, quality 75) writes for a fixed-width image, rebuilt from the same
 * build-time image config, so the desktop preload requests exactly the file the <img> picks. Undefined when the
 * config isn't available (the image then simply isn't preloaded).
 */
const nextImageSrcSet = (src: string, width: number): string | undefined => {
    const raw = process.env.__NEXT_IMAGE_OPTS as unknown as {deviceSizes?: number[]; imageSizes?: number[]; path?: string} | undefined;
    if (!raw?.deviceSizes || !raw.imageSizes) return undefined;
    const allSizes = [...raw.deviceSizes, ...raw.imageSizes].sort((a, b) => a - b);
    const pick = (w: number) => allSizes.find((size) => size >= w) ?? allSizes[allSizes.length - 1];
    const widths = [...new Set([width, width * 2].map(pick))];
    const path = raw.path || '/_next/image';
    return widths.map((w, i) => `${path}?url=${encodeURIComponent(src)}&w=${w}&q=75 ${i + 1}x`).join(', ');
};

const ArrowIcon = cdnImage('career/arrow.webp');
const check = cdnImage('Home-page/check.svg');
const circle = cdnImage('banner/circle.webp');
const bgImg = cdnImage('banner/how-to.svg');

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface BannerDataPoint {
    heading?: React.ReactNode;
    subheading?: React.ReactNode;
}

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div' | 'span';

export interface BannerProps {
    image?: string | StaticImageData;
    /** Button click handler, or — with `meetingLink` — the URL the button links to. */
    handleClickOpen?: string | ((event: React.MouseEvent<HTMLButtonElement>) => void);
    heading?: string;
    subheading?: React.ReactNode;
    button?: React.ReactNode;
    bannerClass?: string;
    className?: string;
    bannerBackground?: string;
    subheading2?: React.ReactNode;
    arrowVisible?: boolean | string;
    DataPoints?: BannerDataPoint[];
    /** Only its truthiness is read (shows the review badges). */
    bannerIcons?: unknown;
    circleAnimation?: boolean;
    meetingLink?: boolean;
    priority?: boolean;
    headingLevel?: HeadingTag;
    /**
     * Extra class on the image box (`top-banner`), the replacement for the MUI version's `bannerImageBoxSx`: a page
     * moves its overrides (`& img`, `@media`, …) into its own CSS module and passes the class here.
     */
    bannerImageBoxClassName?: string;
    /** Passed by many pages but never read (the image alt is `heading`), as in the original. */
    alt?: string;
    /** Passed by one page but never read, as in the original. */
    gridrootclass?: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const hiddenSpanStyle: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap',
    border: 0
};

const badgeLinkStyle: React.CSSProperties = {display: 'flex', alignItems: 'center', textDecoration: 'none'};

const badgeImageStyle: React.CSSProperties = {width: '100%', height: 'auto', objectFit: 'contain', display: 'block'};

const reviewLinks = [
    {
        image: clutchBadge,
        alt: 'Clutch Review Badge',
        href: 'https://clutch.co/profile/debut-infotech#reviews',
        hiddenText: 'Review us on Clutch - Debut Infotech'
    },
    {
        image: goodfirmsBadge,
        alt: 'GoodFirms Review Badge',
        href: 'https://www.goodfirms.co/company/debut-infotech',
        hiddenText: 'Review us on GoodFirms - Debut Infotech'
    },
    {
        image: topDevBadge,
        alt: 'TopDevelopers Review Badge',
        href: 'https://www.topdevelopers.co/profile/debut-infotech',
        hiddenText: 'TopDevelopers - Debut Infotech'
    }
];

// ─── Component ──────────────────────────────────────────────────────────────────

const Banner = ({
    image,
    handleClickOpen,
    heading,
    subheading,
    button,
    bannerClass,
    className,
    bannerBackground,
    subheading2,
    arrowVisible,
    DataPoints,
    bannerIcons,
    circleAnimation,
    meetingLink,
    priority = false,
    headingLevel = 'h1',
    bannerImageBoxClassName
}: BannerProps) => {
    const HeadingTagName = headingLevel;
    const imageSrc = typeof image === 'string' ? image : (image as StaticImageData | undefined)?.src;
    const desktopSrcSet = priority && imageSrc ? nextImageSrcSet(imageSrc, BANNER_IMAGE_WIDTH) : undefined;

    return (
        <div className={`banner-section ${styles.section}`}>
            <div className="app-bar-main">
                {/* The literal `${bannerBackground}` (it reads "undefined" when absent) matches the original class list. */}
                <div className={`banner-main-outer ${bannerBackground} ${styles.outer}`}>
                    <Container>
                        <div className="banner-wrapper">
                            <div className={styles.grid}>
                                {/* LEFT CONTENT */}
                                <div className={`banner-left-grid ${styles.leftItem}`}>
                                    <div className={`left-banner-content ${styles.leftContent}`}>
                                        <HeadingTagName className={`banner-heading font-headers ${styles.heading}`}>
                                            {heading}
                                        </HeadingTagName>

                                        <div className={`banner-subheading banner-contact-subheading ${styles.subheadingBox}`}>
                                            <p className={`header-text ${styles.headerText}`}>{subheading}</p>
                                        </div>

                                        {subheading2 && (
                                            <div className={`banner-subheading banner-contact-subheading ${styles.subheadingBox}`}>
                                                <div className={`header-text ${styles.headerText}`}>{subheading2}</div>
                                            </div>
                                        )}

                                        {button && (
                                            <div className="banner-contact-btn">
                                                {meetingLink ? (
                                                    <ArrowButton variant="primary" href={handleClickOpen as string} target="_blank">
                                                        {button}
                                                    </ArrowButton>
                                                ) : (
                                                    <ArrowButton
                                                        variant="primary"
                                                        onClick={typeof handleClickOpen === 'function' ? handleClickOpen : undefined}
                                                    >
                                                        {button}
                                                    </ArrowButton>
                                                )}
                                            </div>
                                        )}

                                        {DataPoints && (
                                            <div className={`banner-point-wrapper ${styles.points}`}>
                                                {DataPoints.map((item, index) => (
                                                    <div key={index + 1} className={`banner-points-item-wrapper ${styles.pointItem}`}>
                                                        <OptimizedImage src={check} alt="white-check" priority={priority} />

                                                        <div className="banner-points-content">
                                                            <p className={styles.pointHeading}>{item?.heading}</p>
                                                            <p className={styles.pointText}>{item?.subheading}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {arrowVisible && (
                                        <div className="banner-icon">
                                            <OptimizedImage src={ArrowIcon} alt="arrow-icon" priority={priority} />
                                        </div>
                                    )}
                                </div>

                                {/* RIGHT IMAGE */}
                                <div className={`banner-img-outer ${styles.rightItem}`}>
                                    {image && (
                                        <div
                                            className={`top-banner ${bannerClass || ''} ${styles.topBanner}${
                                                bannerImageBoxClassName ? ` ${bannerImageBoxClassName}` : ''
                                            }`}
                                        >
                                            {priority && desktopSrcSet && (
                                                <Head>
                                                    <link
                                                        rel="preload"
                                                        as="image"
                                                        imageSrcSet={desktopSrcSet}
                                                        media={DESKTOP_BANNER_MEDIA}
                                                        {...HIGH_FETCH_PRIORITY}
                                                    />
                                                </Head>
                                            )}
                                            <Image
                                                src={image}
                                                alt={heading as string}
                                                className={`banner-img ${className} ${styles.bannerImg}`}
                                                width={BANNER_IMAGE_WIDTH}
                                                height={532}
                                                // Still fetched straight away (never lazy), but not at top priority on phones.
                                                loading={priority ? 'eager' : undefined}
                                                placeholder={
                                                    typeof image === 'object' && 'blurDataURL' in image && image.blurDataURL
                                                        ? 'blur'
                                                        : undefined
                                                }
                                            />

                                            {circleAnimation && (
                                                <div>
                                                    <div className={`circle-banner-rotator ${styles.circleRotator}`}>
                                                        <Image src={circle} alt="circle Image" />
                                                    </div>

                                                    <div className={`background-color-banner ${styles.bgBanner}`}>
                                                        <Image src={bgImg} alt="bg-image" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* REVIEW BADGES */}
                        {Boolean(bannerIcons) && (
                            <div className={styles.badges}>
                                {reviewLinks.map((item, index) => (
                                    <a key={index + 1} href={item.href} target="_blank" rel="noopener noreferrer" style={badgeLinkStyle}>
                                        <div className={styles.badgeBox}>
                                            <Image
                                                src={item.image}
                                                alt={item.alt}
                                                width={240}
                                                height={70}
                                                priority={priority}
                                                style={badgeImageStyle}
                                            />
                                        </div>

                                        <span style={hiddenSpanStyle}>{item.hiddenText}</span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </Container>
                </div>
            </div>
        </div>
    );
};

export default memo(Banner);
